import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, uploadReceipt, receiptHref } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Paperclip, Droplets, Search, Sparkles, History, Layers } from "lucide-react";
import AddServiceDialog from "@/mechanic/components/AddServiceDialog";

export const WO_STATUSES = ["Estimate", "Approved", "In Progress", "Waiting on Parts", "Ready", "Picked Up", "Closed"];

const blank = {
  vehicle_source: "", vehicle_ref_id: "", vehicle_label: "",
  customer_id: "", customer_name: "", status: "In Progress", is_oil_change: false,
  complaint: "", diagnosis: "", notes: "", mechanic_id: "", mechanic_name: "",
  labor_lines: [], parts_lines: [], oil_lines: [], attachments: [],
  lift: "", shop_supply_fee: "", discount: "", promised_date: "", mileage: "",
  billing_mode: "itemized", flat_job_price: "", tax_applied: true,
  service_date: new Date().toISOString().slice(0, 10),
};

const normGroup = (l, fallback = "Service") => (l.job_group || "").trim() || fallback;
const normLines = (ls, fallback = "Service") => (ls || []).map((l) => ({ ...l, job_group: normGroup(l, fallback) }));

const quickBlank = {
  // Customer
  customer_name: "", customer_phone: "", customer_email: "",
  // Vehicle
  year: "", make: "", model: "", trim: "", color: "",
  plate: "", vin: "", mileage: "", notes: "",
};

const legacyToLines = (w) => {
  let labor = (w.labor_lines || []).map((l) => ({ ...l }));
  if (!labor.length) {
    const manual = Number(w.labor_cost || 0), h = Number(w.labor_hours || 0), r = Number(w.labor_rate || 0);
    if (manual > 0) labor = [{ description: "Labor", hours: 1, rate_cost: manual, rate_charge: manual, tech_id: w.mechanic_id || "", tech_name: w.mechanic_name || "" }];
    else if (h > 0) labor = [{ description: "Labor", hours: h, rate_cost: r, rate_charge: r, tech_id: w.mechanic_id || "", tech_name: w.mechanic_name || "" }];
  }
  let parts = (w.parts_lines || []).map((l) => ({ ...l }));
  if (!parts.length && (w.parts || []).length) {
    parts = w.parts.map((p) => ({ part_id: "", name: p.name, part_number: "", qty: 1, unit_cost: Number(p.cost || 0), unit_price: Number(p.cost || 0) }));
  }
  return { labor, parts };
};

export default function WorkOrderDialog({ open, onOpenChange, editing, presetVehicle, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState(quickBlank);
  const [quickSaving, setQuickSaving] = useState(false);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [selectedOilId, setSelectedOilId] = useState("");
  // Jobs — each job groups its labor + parts lines via the `job_group` field.
  const [jobs, setJobs] = useState([]);

  // Apply a canned / past service block — APPEND lines instead of replacing so
  // multiple services can stack on one WO (Identifix behaviour).
  const applyServiceBlock = (block) => {
    const fallback = (block.name || "").trim() || "Service";
    setForm((f) => ({
      ...f,
      labor_lines: [...f.labor_lines, ...normLines(block.labor_lines, fallback)],
      parts_lines: [...f.parts_lines, ...normLines(block.parts_lines, fallback)],
      oil_lines: [...f.oil_lines, ...(block.oil_lines || [])],
      is_oil_change: f.is_oil_change || !!block.is_oil_change,
      complaint: f.complaint || block.complaint || "",
      shop_supply_fee: Number(f.shop_supply_fee || 0) + Number(block.shop_supply_fee || 0) || f.shop_supply_fee,
    }));
  };

  const { data: techs = [] } = useQuery({ queryKey: ["mech-techs"], queryFn: async () => (await api.get("/techs")).data, enabled: open });
  const { data: invParts = [] } = useQuery({ queryKey: ["mech-parts-all"], queryFn: async () => (await api.get("/parts")).data, enabled: open });
  const { data: oils = [] } = useQuery({ queryKey: ["mech-oils"], queryFn: async () => (await api.get("/oils")).data, enabled: open });
  const { data: rental = [] } = useQuery({ queryKey: ["mech-rental"], queryFn: async () => (await api.get("/rental-fleet")).data, enabled: open && !presetVehicle });
  const { data: dealer = [] } = useQuery({ queryKey: ["mech-dealer"], queryFn: async () => (await api.get("/dealership-cars")).data, enabled: open && !presetVehicle });
  const { data: ext = [] } = useQuery({ queryKey: ["mech-ext"], queryFn: async () => (await api.get("/vehicles")).data, enabled: open && !presetVehicle });

  // Load the full RO (state + linked invoice) when editing an existing WO
  const { data: suppliers = [] } = useQuery({
    queryKey: ["mech-suppliers-list"], enabled: open,
    queryFn: async () => (await api.get("/suppliers")).data,
  });
  const { data: shopSettings } = useQuery({
    queryKey: ["mech-settings"], enabled: open,
    queryFn: async () => (await api.get("/settings")).data,
  });

  // Load the full RO (state + linked invoice) when editing an existing WO
  const { data: fullWo, refetch: refetchWo } = useQuery({
    queryKey: ["mech-wo-full", editing?.id],
    enabled: !!(open && editing?.id),
    queryFn: async () => (await api.get(`/work-orders/${editing.id}`)).data,
  });
  const roState = fullWo?.ro_state || "estimate";
  const linkedInvoice = fullWo?.linked_invoice || null;

  const advance = useMutation({
    mutationFn: (target_state) => api.post(`/work-orders/${editing.id}/advance`, { target_state: target_state || "" }),
    onSuccess: async () => { await refetchWo(); toast.success("RO state updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not advance state"),
  });
  const postPayment = useMutation({
    mutationFn: (body) => api.post(`/work-orders/${editing.id}/payments`, body),
    onSuccess: async () => { await refetchWo(); toast.success("Payment posted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const delPayment = useMutation({
    mutationFn: (pid) => api.delete(`/work-orders/${editing.id}/payments/${pid}`),
    onSuccess: async () => { await refetchWo(); toast.success("Payment removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const { labor, parts } = legacyToLines(editing);
      const nl = normLines(labor), np = normLines(parts);
      setForm({
        ...blank, ...editing,
        labor_lines: nl, parts_lines: np,
        oil_lines: (editing.oil_lines || []).map((l) => ({ ...l })),
        attachments: (editing.attachments || []).map((a) => ({ ...a })),
        lift: editing.lift ?? "", shop_supply_fee: editing.shop_supply_fee || "", discount: editing.discount || "",
        promised_date: editing.promised_date || "", mileage: editing.mileage ?? "",
        billing_mode: editing.billing_mode || "itemized",
        flat_job_price: editing.flat_job_price ?? "",
        tax_applied: editing.tax_applied !== false,
      });
      const seen = [];
      [...nl, ...np].forEach((l) => { if (!seen.includes(l.job_group)) seen.push(l.job_group); });
      setJobs(seen);
    } else if (presetVehicle) {
      setForm({ ...blank, vehicle_source: presetVehicle.source, vehicle_ref_id: presetVehicle.ref_id, vehicle_label: presetVehicle.label });
      setJobs([]);
    } else {
      setForm(blank);
      setJobs([]);
    }
  }, [open, editing, presetVehicle]);

  // Keep the jobs list in sync when lines arrive from canned jobs / presets /
  // past services — any new job_group gets its own job card automatically.
  useEffect(() => {
    const groups = [];
    [...form.labor_lines, ...form.parts_lines].forEach((l) => {
      const g = l.job_group ?? "";
      if (!groups.includes(g)) groups.push(g);
    });
    setJobs((prev) => {
      const missing = groups.filter((g) => !prev.includes(g));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [form.labor_lines, form.parts_lines]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isInternal = form.vehicle_source === "rental" || form.vehicle_source === "dealership";

  // Past services on the selected vehicle (only for NEW WOs — hidden while editing)
  const { data: pastServices = [] } = useQuery({
    queryKey: ["mech-wo-past", form.vehicle_source, form.vehicle_ref_id],
    enabled: !!(form.vehicle_source && form.vehicle_ref_id && !editing),
    queryFn: async () =>
      (await api.get("/work-orders", { params: { vehicle_source: form.vehicle_source, vehicle_ref_id: form.vehicle_ref_id } })).data,
  });

  const reusePastService = (w) => {
    // Copy labor + parts + oil lines (deep clone, drop attachments so we don't
    // duplicate receipts), and carry over the complaint as a hint.
    setForm((f) => ({
      ...f,
      labor_lines: normLines(w.labor_lines),
      parts_lines: normLines(w.parts_lines),
      oil_lines: (w.oil_lines || []).map((l) => ({ ...l })),
      is_oil_change: !!w.is_oil_change,
      complaint: f.complaint || w.complaint || "",
      shop_supply_fee: f.shop_supply_fee || w.shop_supply_fee || "",
    }));
    toast.success(`Copied ${(w.labor_lines || []).length + (w.parts_lines || []).length + (w.oil_lines || []).length} line(s) from ${w.service_date || "past job"}`);
  };

  const vehicleOptions = [
    ...rental.map((v) => ({ key: `rental:${v.id}`, source: "rental", ref: v.id, label: `🚗 ${v.label} · ${v.plate || ""}`.trim(), raw: v.label })),
    ...dealer.map((v) => ({ key: `dealership:${v.id}`, source: "dealership", ref: v.id, label: `🏬 ${v.label} · ${v.stock_number || ""}`.trim(), raw: v.label })),
    ...ext.map((v) => ({ key: `external:${v.id}`, source: "external", ref: v.id, label: `👤 ${v.year || ""} ${v.make} ${v.model} · ${v.customer_name || ""}`.trim(), raw: `${v.year || ""} ${v.make} ${v.model}`.trim(), customer_id: v.customer_id, customer_name: v.customer_name })),
  ];

  const onPickVehicle = (key) => {
    const opt = vehicleOptions.find((o) => o.key === key);
    if (opt) setForm((f) => ({ ...f, vehicle_source: opt.source, vehicle_ref_id: opt.ref, vehicle_label: opt.raw, customer_id: opt.customer_id || "", customer_name: opt.customer_name || "" }));
  };

  const decodeVin = async () => {
    const vin = (quick.vin || "").trim();
    if (vin.length < 5) { toast.error("Enter a VIN to decode"); return; }
    setVinDecoding(true);
    try {
      const d = (await api.post("/vin/decode", { vin })).data;
      setQuick((q) => ({
        ...q,
        vin: d.vin || q.vin,
        year: d.year ? String(d.year) : q.year,
        make: d.make || q.make,
        model: d.model || q.model,
        trim: d.trim || q.trim,
      }));
      if (d.note) toast.success(`VIN decoded (partial): ${d.year || ""} ${d.make} ${d.model}`);
      else toast.success(`VIN decoded: ${d.year || ""} ${d.make} ${d.model}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN lookup failed");
    } finally {
      setVinDecoding(false);
    }
  };

  const createQuickCustomerVehicle = async () => {    const name = quick.customer_name.trim();
    const make = quick.make.trim();
    const model = quick.model.trim();
    if (!name) { toast.error("Client name is required"); return; }
    if (!make || !model) { toast.error("Vehicle make and model are required"); return; }
    setQuickSaving(true);
    try {
      const cust = (await api.post("/customers", {
        name,
        phone: quick.customer_phone.trim(),
        email: quick.customer_email.trim(),
      })).data;
      const veh = (await api.post("/vehicles", {
        customer_id: cust.id,
        customer_name: cust.name,
        year: quick.year ? Number(quick.year) : null,
        make, model,
        trim: quick.trim.trim(),
        color: quick.color.trim(),
        plate: quick.plate.trim(),
        vin: quick.vin.trim(),
        mileage: quick.mileage ? Number(quick.mileage) : null,
        notes: "",
      })).data;
      // Refresh vehicle & customer lists in the picker
      await qc.invalidateQueries({ queryKey: ["mech-ext"] });
      await qc.invalidateQueries({ queryKey: ["mech-customers"] });
      // Auto-select the new vehicle on the WO
      const yearMakeModel = `${quick.year || ""} ${make} ${model}`.trim();
      setForm((f) => ({
        ...f,
        vehicle_source: "external",
        vehicle_ref_id: veh.id,
        vehicle_label: yearMakeModel,
        customer_id: cust.id,
        customer_name: cust.name,
        mileage: quick.mileage ? Number(quick.mileage) : f.mileage,
      }));
      setQuickOpen(false);
      setQuick(quickBlank);
      toast.success(`Added ${cust.name} + ${yearMakeModel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save new client / car");
    } finally {
      setQuickSaving(false);
    }
  };

  // --- line helpers ---
  const updLine = (key, i, patch) => setForm((f) => {
    const a = [...f[key]]; a[i] = { ...a[i], ...patch }; return { ...f, [key]: a };
  });
  const rmLine = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const defaultRate = () => Number(shopSettings?.default_labor_rate) || 120;
  const addLaborTo = (g) => set("labor_lines", [...form.labor_lines, { description: "", hours: 1, rate_cost: 0, rate_charge: defaultRate(), tech_id: "", tech_name: "", job_group: g }]);
  const addPartTo = (g) => set("parts_lines", [...form.parts_lines, { part_id: "", name: "", part_number: "", qty: 1, unit_cost: "", unit_price: "", supplier: "", job_group: g }]);
  const addOil = () => set("oil_lines", [...form.oil_lines, { oil_id: "", oil_name: "", litres: "", cost_per_litre: 0 }]);

  // --- job helpers ---
  const addJob = () => {
    let name = "New Job", i = 2;
    while (jobs.includes(name)) name = `New Job ${i++}`;
    setJobs((a) => [...a, name]);
  };
  const renameJob = (ji, nu) => {
    const old = jobs[ji];
    if (nu === old) return;
    setJobs((a) => a.map((x, idx) => (idx === ji ? nu : x)));
    setForm((f) => ({
      ...f,
      labor_lines: f.labor_lines.map((l) => ((l.job_group ?? "") === old ? { ...l, job_group: nu } : l)),
      parts_lines: f.parts_lines.map((l) => ((l.job_group ?? "") === old ? { ...l, job_group: nu } : l)),
    }));
  };
  const removeJob = (ji) => {
    const g = jobs[ji];
    const count = form.labor_lines.filter((l) => (l.job_group ?? "") === g).length
      + form.parts_lines.filter((l) => (l.job_group ?? "") === g).length;
    if (count > 0 && !window.confirm(`Remove job "${g}" and its ${count} line(s)?`)) return;
    setJobs((a) => a.filter((_, idx) => idx !== ji));
    setForm((f) => ({
      ...f,
      labor_lines: f.labor_lines.filter((l) => (l.job_group ?? "") !== g),
      parts_lines: f.parts_lines.filter((l) => (l.job_group ?? "") !== g),
    }));
  };

  // Auto-select the first available oil so presets are ready to click.
  useEffect(() => {
    if (!selectedOilId && oils.length > 0) setSelectedOilId(oils[0].id);
  }, [oils, selectedOilId]);

  // Oil change preset — one button. Drops TWO blank parts_lines (Oil + Oil
  // filter) that the tech fills in themselves (part #, qty, cost, sell,
  // supplier). Also drops an EMPTY oil_line pre-seeded with the currently
  // selected oil (or the first oil in inventory) so the "Oil used" panel
  // below opens immediately — tech just types litres.
  const addOilChangePreset = () => {
    const oil = oils.find((o) => o.id === selectedOilId) || oils[0] || null;
    setForm((f) => ({
      ...f,
      is_oil_change: true,
      parts_lines: [
        ...f.parts_lines,
        { part_id: "", name: "Oil", part_number: "", qty: "", unit_cost: "", unit_price: "", supplier: "", job_group: "Engine Oil & Filter R&R" },
        { part_id: "", name: "Oil filter", part_number: "", qty: "", unit_cost: "", unit_price: "", supplier: "", job_group: "Engine Oil & Filter R&R" },
      ],
      oil_lines: [
        ...f.oil_lines,
        { oil_id: oil?.id || "", oil_name: oil?.name || "", litres: "", cost_per_litre: Number(oil?.cost_per_litre || 0) },
      ],
    }));
    if (oil) setSelectedOilId(oil.id);
    toast.success(
      oil
        ? `Oil + Oil filter added. ${oil.name} selected below — enter the litres you used and it'll deduct from inventory on save.`
        : "Oil + Oil filter added. Add an oil to Inventory → Oils & Fluids to enable inventory deduction."
    );
  };

  const pickTech = (i, id) => {
    const t = techs.find((x) => x.id === id);
    updLine("labor_lines", i, { tech_id: id, tech_name: t?.name || "", rate_cost: form.labor_lines[i].rate_cost || t?.hourly_rate || "", rate_charge: form.labor_lines[i].rate_charge || t?.charge_rate || t?.hourly_rate || "" });
  };
  const pickPart = (i, id) => {
    if (id === "__custom") { updLine("parts_lines", i, { part_id: "" }); return; }
    const p = invParts.find((x) => x.id === id);
    if (p) updLine("parts_lines", i, { part_id: id, name: p.name, part_number: p.part_number || "", unit_cost: p.unit_cost || "", unit_price: p.sell_price || p.unit_cost || "" });
  };
  const pickOil = (i, id) => {
    const o = oils.find((x) => x.id === id);
    if (o) updLine("oil_lines", i, { oil_id: id, oil_name: o.name, cost_per_litre: Number(o.cost_per_litre || 0) });
  };

  const onAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadReceipt(file);
      set("attachments", [...form.attachments, { name: r.name || file.name, url: r.url }]);
      toast.success("Receipt attached");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  // --- totals ---
  const n = (v) => Number(v || 0);
  const partsCost = form.parts_lines.reduce((s, l) => s + n(l.qty) * n(l.unit_cost), 0);
  const partsCharge = form.parts_lines.reduce((s, l) => s + n(l.qty) * (n(l.unit_price) || n(l.unit_cost)), 0);
  const laborCost = form.labor_lines.reduce((s, l) => s + n(l.hours) * n(l.rate_cost), 0);
  const laborCharge = form.labor_lines.reduce((s, l) => s + n(l.hours) * (n(l.rate_charge) || n(l.rate_cost)), 0);
  const oilCost = form.oil_lines.reduce((s, l) => s + n(l.litres) * n(l.cost_per_litre), 0);
  const totalCost = partsCost + laborCost + oilCost;
  const flatMode = form.billing_mode === "flat";
  const flatPrice = n(form.flat_job_price);
  // Customer charge (fleet jobs are billed to the car's owner company the same way):
  //   • flat-price mode → single job price the shop quoted
  //   • itemized (default) → parts sell + labor sell + shop fee − discount
  const charge = flatMode ? flatPrice : partsCharge + laborCharge + n(form.shop_supply_fee) - n(form.discount);
  const profit = charge - totalCost;
  const hst = form.tax_applied ? charge * 0.13 : 0;
  const totalWithTax = charge + hst;

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        status: form.status,
        labor_hours: 0, labor_rate: 0, labor_cost: 0, parts: [],
        mechanic_id: form.labor_lines[0]?.tech_id || form.mechanic_id || "",
        mechanic_name: form.labor_lines[0]?.tech_name || form.mechanic_name || "",
        lift: form.lift === "" || form.lift === "none" ? null : Number(form.lift),
        shop_supply_fee: n(form.shop_supply_fee), discount: n(form.discount),
        billing_mode: form.billing_mode || "itemized",
        flat_job_price: n(form.flat_job_price),
        tax_applied: !!form.tax_applied,
        mileage: form.mileage === "" ? null : Number(form.mileage),
        labor_lines: form.labor_lines.filter((l) => n(l.hours) > 0 || l.description).map((l) => ({ ...l, hours: n(l.hours), rate_cost: n(l.rate_cost), rate_charge: n(l.rate_charge) })),
        parts_lines: form.parts_lines.filter((l) => l.name || n(l.unit_cost) > 0 || n(l.unit_price) > 0).map((l) => ({ ...l, qty: n(l.qty), unit_cost: n(l.unit_cost), unit_price: n(l.unit_price) })),
        oil_lines: form.oil_lines.filter((l) => l.oil_id && n(l.litres) > 0).map((l) => ({ ...l, litres: n(l.litres), cost_per_litre: n(l.cost_per_litre) })),
      };
      if (editing) return (await api.put(`/work-orders/${editing.id}`, body)).data;
      return (await api.post("/work-orders", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(editing ? "Work order updated" : "Work order created");
      onOpenChange(false);
      onSaved && onSaved();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save work order"),
  });

  const statusOptions = WO_STATUSES.includes(form.status) || !form.status ? WO_STATUSES : [form.status, ...WO_STATUSES];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit Work Order" : "New Work Order"}</DialogTitle>
          <DialogDescription>Labor, parts and oil usage — costs auto-roll into shop metrics.</DialogDescription>
        </DialogHeader>

        {editing && (
          <ROStepper
            state={roState}
            invoice={linkedInvoice}
            advancing={advance.isPending}
            onAdvance={(target) => advance.mutate(target)}
          />
        )}

        <div className="grid grid-cols-2 gap-4 py-2">
          {!presetVehicle && !editing && (
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="label-mono">Vehicle *</Label>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => { setQuickOpen((v) => !v); setQuick(quickBlank); }}
                  data-testid="wo-add-new-vehicle"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {quickOpen ? "Cancel new client" : "New client & car"}
                </Button>
              </div>
              <Select
                value={form.vehicle_ref_id ? `${form.vehicle_source}:${form.vehicle_ref_id}` : undefined}
                onValueChange={onPickVehicle}
                disabled={quickOpen}
              >
                <SelectTrigger data-testid="wo-vehicle-select"><SelectValue placeholder="Choose a car (rental / dealership / customer)" /></SelectTrigger>
                <SelectContent>
                  {vehicleOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>

              {quickOpen && (
                <div className="border border-dashed border-primary/40 bg-primary/5 rounded-md p-3 space-y-3" data-testid="wo-quick-panel">
                  <p className="text-xs text-muted-foreground -mb-1">New walk-in — enter the client and their car once, and it will be saved to Customers + Vehicles.</p>
                  <div className="grid grid-cols-6 gap-2">
                    <Input className="col-span-3" placeholder="Client name *" value={quick.customer_name} onChange={(e) => setQuick({ ...quick, customer_name: e.target.value })} data-testid="wo-quick-cust-name" />
                    <Input className="col-span-3" placeholder="Phone" value={quick.customer_phone} onChange={(e) => setQuick({ ...quick, customer_phone: e.target.value })} data-testid="wo-quick-cust-phone" />
                    <Input className="col-span-6" placeholder="Email (optional)" value={quick.customer_email} onChange={(e) => setQuick({ ...quick, customer_email: e.target.value })} data-testid="wo-quick-cust-email" />
                    <Input className="col-span-1" placeholder="Year" value={quick.year} onChange={(e) => setQuick({ ...quick, year: e.target.value })} data-testid="wo-quick-year" />
                    <Input className="col-span-2" placeholder="Make *" value={quick.make} onChange={(e) => setQuick({ ...quick, make: e.target.value })} data-testid="wo-quick-make" />
                    <Input className="col-span-2" placeholder="Model *" value={quick.model} onChange={(e) => setQuick({ ...quick, model: e.target.value })} data-testid="wo-quick-model" />
                    <Input className="col-span-1" placeholder="Trim" value={quick.trim} onChange={(e) => setQuick({ ...quick, trim: e.target.value })} data-testid="wo-quick-trim" />
                    <Input className="col-span-2" placeholder="Color" value={quick.color} onChange={(e) => setQuick({ ...quick, color: e.target.value })} data-testid="wo-quick-color" />
                    <Input className="col-span-2" placeholder="Plate" value={quick.plate} onChange={(e) => setQuick({ ...quick, plate: e.target.value })} data-testid="wo-quick-plate" />
                    <Input className="col-span-2" placeholder="Mileage" type="number" value={quick.mileage} onChange={(e) => setQuick({ ...quick, mileage: e.target.value })} data-testid="wo-quick-mileage" />
                    <div className="col-span-6 flex gap-2">
                      <Input className="flex-1" placeholder="VIN (optional — enter to auto-decode Year/Make/Model)" value={quick.vin} onChange={(e) => setQuick({ ...quick, vin: e.target.value.toUpperCase() })} data-testid="wo-quick-vin" />
                      <Button type="button" size="sm" variant="secondary" onClick={decodeVin} disabled={vinDecoding || !quick.vin.trim()} data-testid="wo-quick-vin-decode">
                        <Sparkles className="h-3.5 w-3.5 mr-1" />
                        {vinDecoding ? "Decoding…" : "Decode VIN"}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="ghost" onClick={() => setQuickOpen(false)}>Cancel</Button>
                    <Button type="button" size="sm" onClick={createQuickCustomerVehicle} disabled={quickSaving} data-testid="wo-quick-save">
                      {quickSaving ? "Saving…" : "Save & use for this job"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {(presetVehicle || editing) && (
            <div className="col-span-2 text-sm"><span className="text-muted-foreground">Vehicle: </span><b>{form.vehicle_label || presetVehicle?.label}</b> <span className="text-xs text-muted-foreground">({form.vehicle_source}{isInternal ? " · fleet — billed to the owner company" : " · customer — billed"})</span></div>
          )}
          {form.vehicle_ref_id && !editing && !presetVehicle && (
            <p className="col-span-2 text-xs text-muted-foreground -mt-2">{isInternal ? "Fleet job — tracks your cost AND what you bill the car's owner company → profit." : "Customer job — tracks your cost AND the billed amount → profit."}</p>
          )}

          {/* BILLING MODE — Itemized (markup on parts + labor) vs Flat job price */}
          <div className="col-span-2 rounded-md border border-border bg-secondary/20 p-3 space-y-2" data-testid="wo-billing-mode-row">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="label-mono">Billing mode</Label>
                <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
                  <button
                    type="button"
                    onClick={() => set("billing_mode", "itemized")}
                    className={`px-3 py-1.5 text-xs font-medium ${form.billing_mode !== "flat" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-accent"}`}
                    data-testid="wo-billing-itemized"
                  >Itemized (markup)</button>
                  <button
                    type="button"
                    onClick={() => set("billing_mode", "flat")}
                    className={`px-3 py-1.5 text-xs font-medium border-l border-border ${form.billing_mode === "flat" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-accent"}`}
                    data-testid="wo-billing-flat"
                  >Flat job price</button>
                </div>
              </div>
              {flatMode ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <Label className="text-sm w-32">Job price ($) *</Label>
                  <Input
                    type="number" step="0.01" placeholder="Total price you're charging (e.g. 500)"
                    value={form.flat_job_price} onChange={(e) => set("flat_job_price", e.target.value)}
                    className="max-w-xs" data-testid="wo-flat-job-price"
                  />
                  <span className="text-xs text-muted-foreground">Parts / labor cost still tracked for profit math — the {isInternal ? "owner" : "customer"} only sees this single price on the invoice.</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{isInternal ? "The car's owner company" : "Customer"} is billed the sum of each part&apos;s <b>Sell</b> price + labor at your <b>Rate</b> + shop fee − discount.</p>
              )}
            </div>

          {form.vehicle_ref_id && !editing && pastServices.length > 0 && (
            <div className="col-span-2 border border-border rounded-md p-3 bg-secondary/20" data-testid="past-services-panel">
              <div className="flex items-center gap-2 mb-2 label-mono text-xs text-muted-foreground">
                <History className="h-3.5 w-3.5" />
                Past services on this vehicle — click to re-use lines
              </div>
              <div className="flex flex-wrap gap-2">
                {pastServices.slice(0, 3).map((w) => {
                  const total = (w.labor_lines || []).length + (w.parts_lines || []).length + (w.oil_lines || []).length;
                  const summary = (w.complaint || w.diagnosis || "").slice(0, 40) || `${total} line${total === 1 ? "" : "s"}`;
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => reusePastService(w)}
                      className="text-left border border-border rounded-md px-3 py-2 text-xs hover:border-primary/60 hover:bg-primary/5 transition-colors max-w-xs"
                      data-testid={`past-service-${w.id}`}
                      title={`Re-use the ${total} line(s) from this job`}
                    >
                      <div className="font-heading truncate">{summary}</div>
                      <div className="text-[10px] text-muted-foreground font-mono truncate">
                        {w.service_date || w.created_at?.slice(0, 10)} · {w.status || "—"} · {total} line{total === 1 ? "" : "s"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="label-mono">Service Date</Label>
            <Input type="date" value={form.service_date} onChange={(e) => set("service_date", e.target.value)} data-testid="wo-date" />
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Promised completion</Label>
            <Input type="date" value={form.promised_date} onChange={(e) => set("promised_date", e.target.value)} data-testid="wo-promised-date" />
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="wo-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Lift / Bay</Label>
            <Select value={form.lift === null || form.lift === "" ? "none" : String(form.lift)} onValueChange={(v) => set("lift", v)}>
              <SelectTrigger data-testid="wo-lift-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not on a lift</SelectItem>
                {[1, 2, 3].map((l) => <SelectItem key={l} value={String(l)}>Lift {l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Mileage at service</Label>
            <Input type="number" placeholder="e.g. 84500 (km or mi)" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="wo-mileage" />
          </div>
          <div className="space-y-2">
            <Label className="label-mono">Complaint / Work requested</Label>
            <Input value={form.complaint} onChange={(e) => set("complaint", e.target.value)} data-testid="wo-complaint" placeholder="e.g. Oil change + brakes" />
          </div>
          <div className="col-span-2 space-y-2">
            <Label className="label-mono">Diagnosis / Work performed</Label>
            <Textarea placeholder="What did the tech find + do? (visible on the invoice)" value={form.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} data-testid="wo-diagnosis" rows={2} />
          </div>

          {/* Quick-add block: canned / past / scratch */}
          {form.vehicle_ref_id && (
            <div className="col-span-2 border-t border-border pt-3 -mb-1">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => setAddServiceOpen(true)}
                data-testid="wo-add-service"
                className="w-full sm:w-auto"
              >
                <Layers className="h-3.5 w-3.5 mr-1" />+ Add service (canned / past / scratch)
              </Button>
            </div>
          )}

          {/* JOBS — each job groups its own labor + parts lines (Identifix style).
              job names are stored on every line as `job_group`, so the printed
              invoice clusters them under the same headers automatically. */}
          <div className="col-span-2 space-y-3 border-t border-border pt-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="font-semibold">Jobs</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={addOilChangePreset} data-testid="wo-add-oil-change">
                  <Droplets className="h-3 w-3 mr-1 text-blue-500" />+ Oil change
                </Button>
                <Button type="button" size="sm" onClick={addJob} data-testid="wo-add-job"><Plus className="h-3 w-3 mr-1" />Add job</Button>
              </div>
            </div>
            {jobs.length === 0 && (
              <p className="text-xs text-muted-foreground">No jobs yet — click <b>Add job</b> to create one (e.g. "Brake Pads R&amp;R"), then add its labor and parts underneath.</p>
            )}
            {jobs.map((g, ji) => {
              const laborEntries = form.labor_lines.map((l, i) => ({ l, i })).filter(({ l }) => (l.job_group ?? "") === g);
              const partEntries = form.parts_lines.map((l, i) => ({ l, i })).filter(({ l }) => (l.job_group ?? "") === g);
              if (jobs.indexOf(g) !== ji) return null; // duplicate name — lines render once
              const jobCharge = laborEntries.reduce((s, { l }) => s + n(l.hours) * (n(l.rate_charge) || n(l.rate_cost)), 0)
                + partEntries.reduce((s, { l }) => s + n(l.qty) * (n(l.unit_price) || n(l.unit_cost)), 0);
              return (
                <div key={ji} className="rounded-md border border-border p-3 space-y-2 bg-secondary/10" data-testid={`wo-job-${ji}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Job {ji + 1}</span>
                    <Input
                      value={g}
                      onChange={(e) => renameJob(ji, e.target.value)}
                      placeholder="Job name (e.g. Sway Bar Link R&R)"
                      className="h-8 font-medium"
                      data-testid={`wo-job-name-${ji}`}
                    />
                    <span className="text-xs font-mono-data whitespace-nowrap text-muted-foreground" data-testid={`wo-job-total-${ji}`}>{money(jobCharge)}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeJob(ji)} data-testid={`wo-job-remove-${ji}`}><X className="h-4 w-4" /></Button>
                  </div>

                  {laborEntries.map(({ l, i }) => (
                    <div key={`l${i}`} className="grid gap-2 items-center grid-cols-1 lg:[grid-template-columns:minmax(220px,4fr)_120px_140px_40px]">
                      <Input placeholder="Labor description (e.g. Brake pad replacement)" value={l.description} onChange={(e) => updLine("labor_lines", i, { description: e.target.value })} data-testid={`wo-labor-desc-${i}`} />
                      <Input type="number" step="0.25" placeholder="Hours (e.g. 1.5)" value={l.hours} onChange={(e) => updLine("labor_lines", i, { hours: e.target.value })} data-testid={`wo-labor-hours-${i}`} />
                      <Input type="number" step="0.01" placeholder="Rate $/hr (default 120)" value={l.rate_charge} onChange={(e) => updLine("labor_lines", i, { rate_charge: e.target.value })} data-testid={`wo-labor-charge-${i}`} disabled={flatMode} title={flatMode ? "Flat job price mode — labor sell rate isn't billed to the client; cost is still tracked below." : ""} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmLine("labor_lines", i)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}

                  {partEntries.map(({ l, i }) => (
                    <div key={`p${i}`} className="rounded-md border border-border/60 p-2 space-y-2" data-testid={`wo-part-row-${i}`}>
                      <div className="grid gap-2 items-center grid-cols-1 md:[grid-template-columns:minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_40px]">
                        <Input placeholder="Part name (e.g. Brake pads)" value={l.name} onChange={(e) => updLine("parts_lines", i, { name: e.target.value })} data-testid={`wo-part-name-${i}`} />
                        <Input placeholder="Part # (optional)" value={l.part_number} onChange={(e) => updLine("parts_lines", i, { part_number: e.target.value })} data-testid={`wo-part-number-${i}`} />
                        <Select value={l.supplier || undefined} onValueChange={(v) => updLine("parts_lines", i, { supplier: v === "__none" ? "" : v })}>
                          <SelectTrigger data-testid={`wo-part-supplier-${i}`}><SelectValue placeholder="Supplier" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">— None —</SelectItem>
                            {suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="ghost" size="icon" onClick={() => rmLine("parts_lines", i)} data-testid={`wo-part-remove-${i}`}><X className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid gap-2 items-center grid-cols-3">
                        <div>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5 block">Qty</span>
                          <Input type="number" placeholder="Qty (e.g. 1)" value={l.qty} onChange={(e) => updLine("parts_lines", i, { qty: e.target.value })} data-testid={`wo-part-qty-${i}`} />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5 block">Cost $ (what you paid)</span>
                          <Input type="number" step="0.01" placeholder="Cost $ (e.g. 45.00)" value={l.unit_cost} onChange={(e) => updLine("parts_lines", i, { unit_cost: e.target.value })} data-testid={`wo-part-cost-${i}`} />
                        </div>
                        {!flatMode && (
                          <div>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5 block">Sell $ ({isInternal ? "billed to owner" : "client price"})</span>
                            <Input type="number" step="0.01" placeholder="Sell $ (e.g. 80.00)" value={l.unit_price} onChange={(e) => updLine("parts_lines", i, { unit_price: e.target.value })} data-testid={`wo-part-price-${i}`} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-2 pt-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => addLaborTo(g)} data-testid={`wo-job-${ji}-add-labor`}><Plus className="h-3 w-3 mr-1" />Labor</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => addPartTo(g)} data-testid={`wo-job-${ji}-add-part`}><Plus className="h-3 w-3 mr-1" />Part</Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* OIL USED FROM INVENTORY — pick an oil + litres. On save, the
              backend deducts stock automatically. Pricing is handled entirely
              through the Parts lines above (Oil / Oil filter). */}
          <div className="col-span-2 space-y-3 border-t border-border pt-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="font-semibold flex items-center gap-1"><Droplets className="h-4 w-4 text-blue-500" />Oil used (deducts from inventory)</Label>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => set("oil_lines", [...form.oil_lines, { oil_id: selectedOilId || "", oil_name: (oils.find((o) => o.id === selectedOilId)?.name) || "", litres: "", cost_per_litre: Number(oils.find((o) => o.id === selectedOilId)?.cost_per_litre || 0) }])}
                data-testid="wo-add-oil-line"
                disabled={oils.length === 0}
              ><Plus className="h-3 w-3 mr-1" />Add oil line</Button>
            </div>
            {oils.length === 0 && (
              <p className="text-xs text-muted-foreground">No oils in inventory yet — add oil types under <b>Inventory &amp; Expenses → Oils &amp; Fluids</b> to enable inventory tracking.</p>
            )}
            {form.oil_lines.length === 0 ? (
              <p className="text-xs text-muted-foreground">No oil recorded on this WO. Add a line above to deduct oil from your stock.</p>
            ) : (
              <div className="space-y-2">
                {form.oil_lines.map((l, i) => (
                  <div key={i} className="grid gap-2 items-center grid-cols-1 md:[grid-template-columns:minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_40px]" data-testid={`wo-oil-line-${i}`}>
                    <Select
                      value={l.oil_id || undefined}
                      onValueChange={(v) => {
                        const o = oils.find((x) => x.id === v);
                        setSelectedOilId(v);
                        updLine("oil_lines", i, { oil_id: v, oil_name: o?.name || "", cost_per_litre: Number(o?.cost_per_litre || 0) });
                      }}
                    >
                      <SelectTrigger data-testid={`wo-oil-select-${i}`}><SelectValue placeholder="Choose oil from inventory" /></SelectTrigger>
                      <SelectContent>
                        {oils.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name} · {Number(o.stock_litres || 0).toFixed(1)}L in stock
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.1" placeholder="Litres used (e.g. 5)" value={l.litres} onChange={(e) => updLine("oil_lines", i, { litres: e.target.value })} data-testid={`wo-oil-litres-${i}`} />
                    <div className="text-xs text-muted-foreground" data-testid={`wo-oil-cost-${i}`}>@ {money(l.cost_per_litre)}/L</div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => rmLine("oil_lines", i)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_oil_change || form.oil_lines.length > 0} onChange={(e) => set("is_oil_change", e.target.checked)} data-testid="wo-oil-change" />
              Mark as oil change (resets the 45-day rental service clock)
            </label>
          </div>

          {/* CHARGES */}
          <div className="space-y-2 border-t border-border pt-3">
            <Label className="label-mono">Shop supplies fee ($)</Label>
            <Input type="number" placeholder="e.g. 15 (rags, cleaners, disposal)" value={form.shop_supply_fee} onChange={(e) => set("shop_supply_fee", e.target.value)} data-testid="wo-supply-fee" />
          </div>
          <div className="space-y-2 border-t border-border pt-3">
            <Label className="label-mono">Discount ($)</Label>
            <Input type="number" placeholder="e.g. 20 off" value={form.discount} onChange={(e) => set("discount", e.target.value)} data-testid="wo-discount" />
          </div>

          {/* ATTACHMENTS */}
          <div className="col-span-2 space-y-2 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold flex items-center gap-1"><Paperclip className="h-4 w-4" />Receipts / Photos</Label>
              <label className="cursor-pointer">
                <span className="inline-flex items-center h-8 px-3 rounded-md border border-border text-xs font-medium hover:bg-accent">{uploading ? "Uploading…" : "Attach file"}</span>
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={onAttach} data-testid="wo-attach-input" />
              </label>
            </div>
            {form.attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <a href={receiptHref(a.url)} target="_blank" rel="noreferrer" className="text-primary underline truncate" data-testid={`wo-attachment-${i}`}>{a.name || "Receipt"}</a>
                <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => rmLine("attachments", i)}><X className="h-3 w-3" /></Button>
              </div>
            ))}
          </div>

          <div className="col-span-2 space-y-2">
            <Label className="label-mono">Notes</Label>
            <Textarea placeholder="Internal notes for the shop (not on the customer invoice)" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wo-notes" rows={2} />
          </div>

          {/* TOTALS */}
          {/* Tax toggle for this WO — hides HST on the generated invoice when off */}
          <div className="col-span-2 flex items-center justify-between border-t border-border pt-3" data-testid="wo-tax-row">
            <div>
              <div className="text-sm font-medium">Charge HST (13%) on this work order</div>
              <div className="text-xs text-muted-foreground">Turn off for cash / out-of-province / non-taxable {isInternal ? "owners" : "customers"}. The generated invoice will skip the HST line entirely.</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" title="Charge HST on invoice">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!form.tax_applied}
                onChange={(e) => set("tax_applied", e.target.checked)}
                data-testid="wo-tax-toggle"
              />
              <div className="w-11 h-6 bg-secondary border border-border rounded-full peer peer-checked:bg-primary peer-checked:border-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
            </label>
          </div>

          <div className="col-span-2 border-t border-border pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm font-mono-data" data-testid="wo-totals">
            <span>Parts cost: <b>{money(partsCost)}</b></span>
            <span>Labor cost: <b>{money(laborCost)}</b></span>
            <span>Oil cost: <b>{money(oilCost)}</b></span>
            <span>Total cost: <b>{money(totalCost)}</b></span>
            <span className="text-blue-400" data-testid="wo-charge">{flatMode ? "Job price" : (isInternal ? "Billed to owner" : "Customer charge")}: <b>{money(charge)}</b></span>
            <span className={profit >= 0 ? "text-green-400" : "text-red-400"} data-testid="wo-profit">Profit: <b>{money(profit)}</b></span>
            <span className="text-muted-foreground">Margin: <b>{charge > 0 ? `${((profit / charge) * 100).toFixed(1)}%` : "—"}</b></span>
          </div>

          {/* CLIENT INVOICE PREVIEW — live subtotal / HST / total, recalculates
              on every price change so tax is never stale. */}
          <div className="col-span-2 rounded-md border border-border bg-secondary/20 p-3 grid grid-cols-3 gap-2 text-sm font-mono-data" data-testid="wo-invoice-preview">
            <span>Subtotal: <b data-testid="wo-subtotal">{money(charge)}</b></span>
            <span>HST (13%): <b data-testid="wo-hst">{form.tax_applied ? money(hst) : "— off"}</b></span>
            <span className="text-primary">Total incl. tax: <b data-testid="wo-total-with-tax">{money(totalWithTax)}</b></span>
          </div>

          {/* PAYMENT TRAY (only when editing + linked invoice exists OR while at invoice state) */}
          {editing && (roState === "invoice" || roState === "paid" || linkedInvoice) && (
            <PaymentTray
              invoice={linkedInvoice}
              posting={postPayment.isPending}
              onPost={(body) => postPayment.mutate(body)}
              onDelete={(pid) => delPayment.mutate(pid)}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={(!form.vehicle_ref_id) || saveMut.isPending} data-testid="wo-save">
            {editing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <AddServiceDialog
        open={addServiceOpen}
        onOpenChange={setAddServiceOpen}
        onApply={applyServiceBlock}
        vehicleSource={form.vehicle_source}
        vehicleRefId={form.vehicle_ref_id}
      />
    </Dialog>
  );
}

const RO_STATES = [
  { key: "estimate", label: "Estimate" },
  { key: "work_order", label: "Work Order" },
  { key: "invoice", label: "Invoice" },
  { key: "paid", label: "Paid" },
  { key: "closed", label: "Closed" },
];

function ROStepper({ state, invoice, advancing, onAdvance }) {
  const idx = Math.max(0, RO_STATES.findIndex((s) => s.key === state));
  const next = RO_STATES[idx + 1];
  const advanceLabel = {
    estimate: "Start RO",
    work_order: "Convert to invoice",
    invoice: "Mark paid",
    paid: "Close (picked up)",
  }[state];
  return (
    <div className="border border-border bg-secondary/20 rounded-md p-3 mb-2" data-testid="ro-stepper">
      <div className="flex items-center gap-1 flex-wrap">
        {RO_STATES.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <div className={`px-2.5 py-1 rounded-md text-xs label-mono ${i < idx ? "bg-primary/20 text-primary" : i === idx ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground border border-border"}`} data-testid={`ro-pill-${s.key}`}>
              {s.label}
            </div>
            {i < RO_STATES.length - 1 && <span className="mx-1 text-muted-foreground text-xs">›</span>}
          </div>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {invoice?.invoice_number && (
            <span className="text-xs label-mono text-muted-foreground" data-testid="ro-invoice-number">
              Inv: {invoice.invoice_number}
            </span>
          )}
          {next && state !== "closed" && (
            <Button size="sm" onClick={() => onAdvance(next.key)} disabled={advancing} data-testid="ro-advance-btn">
              {advancing ? "…" : advanceLabel || `→ ${next.label}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentTray({ invoice, posting, onPost, onDelete }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const totals = invoice?.totals || { total: 0, amount_paid: 0, balance: 0 };
  const balance = Number(totals.balance || 0);
  const submit = () => {
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Enter an amount"); return; }
    onPost({ amount: n, method: method || "", note: note || "", date: new Date().toISOString().slice(0, 10) });
    setAmount(""); setMethod(""); setNote("");
  };

  return (
    <div className="col-span-2 border-t border-border pt-3" data-testid="payment-tray">
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading text-sm">Payments</div>
        <div className="text-xs label-mono text-muted-foreground">
          Total ${totals.total?.toFixed?.(2) ?? totals.total} · Paid ${totals.amount_paid?.toFixed?.(2) ?? totals.amount_paid} · <b className={balance > 0.01 ? "text-amber-500" : "text-emerald-500"}>Bal ${balance.toFixed(2)}</b>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2 items-center mb-2">
        <Input type="number" className="col-span-3" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wo-pay-amount" />
        <Input className="col-span-3" placeholder="Method (Cash / e-transfer…)" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="wo-pay-method" />
        <Input className="col-span-4" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Button size="sm" className="col-span-1" onClick={() => setAmount(balance > 0 ? balance.toFixed(2) : "")} type="button" title="Fill remaining balance" data-testid="wo-pay-fill">Bal</Button>
        <Button size="sm" className="col-span-1" onClick={submit} disabled={posting || !amount} data-testid="wo-pay-post">Post</Button>
      </div>
      {(invoice?.payments || []).length > 0 && (
        <ul className="border border-border rounded-md divide-y divide-border max-h-40 overflow-y-auto" data-testid="wo-pay-list">
          {invoice.payments.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`wo-pay-${p.id}`}>
              <span className="font-mono">{p.date} · {p.method || "—"}{p.note ? ` · ${p.note}` : ""}</span>
              <span className="flex items-center gap-2">
                <span className="font-mono-data">${Number(p.amount).toFixed(2)}</span>
                <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`wo-pay-del-${p.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

